/**
 * Security Utilities for GoTurkeyTransfer
 * Input sanitization, XSS prevention, and spam protection
 */

// Dangerous patterns to block
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,  // onclick=, onerror=, etc.
  /<iframe/gi,
  /<object/gi,
  /<embed/gi,
  /<link/gi,
  /<meta/gi,
  /data:/gi,
  /vbscript:/gi,
  /expression\s*\(/gi,
  /url\s*\(/gi,
  /<svg[^>]*onload/gi,
  /&#x?[0-9a-f]+;?/gi,  // HTML entities that could be malicious
];

// SQL injection patterns
const SQL_PATTERNS = [
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|TRUNCATE|EXEC|UNION|INTO|FROM|WHERE)\b)/gi,
  /['";\\]/g,
  /--/g,
  /\/\*/g,
  /\*\//g,
];

// Common spam patterns
const SPAM_PATTERNS = [
  /\b(viagra|cialis|casino|poker|lottery|winner|prize|click here|buy now|free money|act now)\b/gi,
  /\b(http:\/\/|https:\/\/)[^\s]{50,}/gi, // Very long URLs
  /((.)\2{4,})/g, // Repeated characters (aaaaaaa)
  /[А-Яа-яЁё]{20,}/g, // Long Cyrillic strings (potential spam)
];

/**
 * Sanitize string input - removes dangerous characters and patterns
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  let sanitized = input.trim();
  
  // Remove dangerous HTML/JS patterns
  DANGEROUS_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });
  
  // Escape HTML entities
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
  
  return sanitized;
};

/**
 * Sanitize for display - lighter sanitization preserving some formatting
 */
export const sanitizeForDisplay = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .trim();
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Validate phone number (international format)
 */
export const isValidPhone = (phone) => {
  // Remove spaces, dashes, parentheses
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // Should be 7-15 digits, optionally starting with +
  const phoneRegex = /^\+?[0-9]{7,15}$/;
  return phoneRegex.test(cleaned);
};

/**
 * Check for SQL injection attempts
 */
export const hasSqlInjection = (input) => {
  if (typeof input !== 'string') return false;
  return SQL_PATTERNS.some(pattern => pattern.test(input));
};

/**
 * Check for XSS attempts
 */
export const hasXssAttempt = (input) => {
  if (typeof input !== 'string') return false;
  return DANGEROUS_PATTERNS.some(pattern => pattern.test(input));
};

/**
 * Check for spam content
 */
export const isSpamContent = (input) => {
  if (typeof input !== 'string') return false;
  
  // Check spam patterns
  if (SPAM_PATTERNS.some(pattern => pattern.test(input))) {
    return true;
  }
  
  // Check for too many URLs
  const urlCount = (input.match(/https?:\/\//g) || []).length;
  if (urlCount > 3) return true;
  
  // Check for excessive caps (shouting)
  const capsRatio = (input.match(/[A-Z]/g) || []).length / input.length;
  if (input.length > 20 && capsRatio > 0.7) return true;
  
  return false;
};

/**
 * Validate and sanitize form data
 */
export const validateFormData = (data) => {
  const errors = [];
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      // Check for attacks
      if (hasXssAttempt(value)) {
        errors.push(`Invalid characters detected in ${key}`);
        continue;
      }
      
      if (hasSqlInjection(value)) {
        errors.push(`Invalid input detected in ${key}`);
        continue;
      }
      
      // Sanitize
      sanitized[key] = sanitizeInput(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return { 
    isValid: errors.length === 0, 
    errors, 
    data: sanitized 
  };
};

/**
 * Rate limiting helper (client-side)
 * Returns true if action should be blocked
 */
const rateLimitStore = {};

export const isRateLimited = (action, maxAttempts = 5, windowMs = 60000) => {
  const now = Date.now();
  const key = action;
  
  if (!rateLimitStore[key]) {
    rateLimitStore[key] = { attempts: 1, windowStart: now };
    return false;
  }
  
  const record = rateLimitStore[key];
  
  // Reset window if expired
  if (now - record.windowStart > windowMs) {
    rateLimitStore[key] = { attempts: 1, windowStart: now };
    return false;
  }
  
  // Increment and check
  record.attempts++;
  
  if (record.attempts > maxAttempts) {
    return true; // Rate limited
  }
  
  return false;
};

/**
 * Generate honeypot field name (changes based on timestamp)
 */
export const getHoneypotFieldName = () => {
  const base = 'website_url_field';
  return base;
};

/**
 * Check if honeypot was filled (bot detection)
 */
export const isHoneypotFilled = (value) => {
  return value && value.trim().length > 0;
};

/**
 * Validate form submission timing (bot detection)
 * Returns true if submission is too fast (likely bot)
 */
export const isSubmissionTooFast = (formLoadTime, minSeconds = 3) => {
  const submissionTime = Date.now();
  const timeTaken = (submissionTime - formLoadTime) / 1000;
  return timeTaken < minSeconds;
};

/**
 * Sanitize object recursively
 */
export const sanitizeObject = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return typeof obj === 'string' ? sanitizeInput(obj) : obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    sanitized[sanitizeInput(key)] = sanitizeObject(value);
  }
  return sanitized;
};

export default {
  sanitizeInput,
  sanitizeForDisplay,
  isValidEmail,
  isValidPhone,
  hasSqlInjection,
  hasXssAttempt,
  isSpamContent,
  validateFormData,
  isRateLimited,
  getHoneypotFieldName,
  isHoneypotFilled,
  isSubmissionTooFast,
  sanitizeObject,
};
